// ENG-5963: the maintenance-window + urgent-escape-hatch decision layer that
// sits ABOVE the existing self-update coalesce gate (self-update-coalesce.ts)
// and the Claude Code updater's idle gate. Pure and dependency-light so the
// gate ordering is unit-testable; the I/O (registry fetch, install) stays at
// the manager call sites.
//
// Ordering at each restart-causing update site:
//   1. urgent dist-tag newer than installed?  → install now, bypass everything
//   2. within the maintenance window?          → no: defer this cycle
//   3. (existing) coalesce / idle gate         → then apply

import {
  isWithinMaintenanceWindow,
  isMaintenanceWindowDisabled,
  type MaintenanceWindowInput,
} from './maintenance-window.js';

/** The resolved window the manager caches off the heartbeat response. */
export type CachedMaintenanceWindow = MaintenanceWindowInput;

/**
 * Window gate for a (non-urgent) restart-causing update.
 *  - 'proceed' → in-window, or gating disabled, or no window known yet
 *    (fail-open so a host that hasn't heard from the API isn't frozen out).
 *  - 'defer'   → a window is known and we're currently outside it.
 */
export function decideMaintenanceWindowGate(opts: {
  window: CachedMaintenanceWindow | null;
  now: Date;
  env?: NodeJS.ProcessEnv;
}): 'proceed' | 'defer' {
  if (isMaintenanceWindowDisabled(opts.env)) return 'proceed';
  if (!opts.window) return 'proceed';
  return isWithinMaintenanceWindow(opts.window, opts.now) ? 'proceed' : 'defer';
}

// ENG-6692: the admin "Update CLI now" one-shot. The API delivers
// hosts.update_requested_at on the heartbeat; the manager runs ONE
// window-bypassing self-update per distinct timestamp (dedup on a persisted
// marker so the fire survives the manager restart the update itself triggers),
// but still defers until the host is idle so a live turn is never interrupted.

export type ForcedUpdateDecision =
  /** No pending "Update CLI now" request on this heartbeat. */
  | 'none'
  /** Already ran for this (or a newer) timestamp — dedup. */
  | 'already-handled'
  /** An agent on the host is mid-turn / recently active — retry next poll. */
  | 'defer-idle'
  /** Run the window-bypassing self-update now (host idle, fresh request). */
  | 'consume';

/**
 * ENG-6771: the outcome of a self-update attempt, so the forced "Update CLI now"
 * caller knows whether to consume its one-shot request.
 *   - 'updated' : committed to an install (restart scheduled).
 *   - 'noop'    : nothing to do (already on the latest in-channel build, dev
 *                 mode, unknown install method, or a non-forced defer) — a
 *                 legitimate completion, so the one-shot is consumed.
 *   - 'failed'  : the attempt failed BEFORE any install committed (registry
 *                 fetch/timeout/non-200, npm install error, post-install smoke
 *                 check) — the one-shot is NOT consumed, so the next idle
 *                 heartbeat retries instead of silently burning the request.
 * Non-forced callers ignore the value.
 */
export type SelfUpdateOutcome = 'updated' | 'noop' | 'failed';

/**
 * ENG-6771: whether a forced "Update CLI now" request should be consumed (its
 * dedup marker advanced so the heartbeat acks it and the API clears
 * hosts.update_requested_at) given the self-update outcome. Only a genuine
 * completion consumes the one-shot: 'updated' or 'noop'. A 'failed' attempt must
 * NOT consume.
 *
 * The bug this guards against (ENG-6771): the old code advanced the dedup marker
 * BEFORE kicking the updater, so a transient npm registry timeout (or any
 * pre-install failure) burned the request silently — the heartbeat acked it, the
 * API cleared the column, and nothing retried while the webapp had shown success.
 */
export function shouldConsumeForcedUpdate(outcome: SelfUpdateOutcome): boolean {
  return outcome !== 'failed';
}

/**
 * Pure decision for the heartbeat consume block: given the timestamp the API
 * delivered, the last one this manager consumed, and whether the host is busy,
 * should we run the forced self-update? Extracted from manager-worker.ts so the
 * dedup + idle ordering is unit-testable (the I/O — persist marker, kick the
 * updater — stays at the call site).
 *
 * Dedup uses `>=` on parsed timestamps so a re-delivered (or out-of-order
 * older) request can't re-fire; only a strictly newer request after a prior
 * consume re-arms. An unparseable timestamp is treated as 'none' (fail safe —
 * never act on a value we can't compare).
 */
export function decideForcedUpdate(opts: {
  requestedAt: string | null | undefined;
  lastProcessedAt: string | null | undefined;
  hostBusy: boolean;
}): ForcedUpdateDecision {
  const { requestedAt, lastProcessedAt, hostBusy } = opts;
  if (!requestedAt) return 'none';
  const requestedMs = Date.parse(requestedAt);
  if (Number.isNaN(requestedMs)) return 'none';
  if (lastProcessedAt) {
    const processedMs = Date.parse(lastProcessedAt);
    if (!Number.isNaN(processedMs) && processedMs >= requestedMs) return 'already-handled';
  }
  if (hostBusy) return 'defer-idle';
  return 'consume';
}

/**
 * Does the `urgent` dist-tag warrant an immediate, window-and-coalesce-bypassing
 * install? True only when an urgent target exists and is strictly newer than
 * what's installed. Channel-independent by construction: there is no channel
 * input, so a channel-pinned host (AGT_CLI_RELEASE_CHANNEL=test) honours urgent
 * exactly like a `latest` host — urgent is a fleet-wide hotfix override.
 * `dev` builds (no injected version) never urgent-upgrade.
 */
export function isUrgentUpgrade(opts: {
  urgentTarget: string | null;
  installed: string;
  isNewer: (installed: string, candidate: string) => boolean;
}): boolean {
  if (!opts.urgentTarget) return false;
  if (opts.installed === 'dev') return false;
  return opts.isNewer(opts.installed, opts.urgentTarget);
}

// ENG-5963 (-4): gating for restart-CAUSING maintenance (agent-session
// restarts), distinct from the update gates above.

/** An agent whose pane.log was touched within this many seconds is "busy"
 * (mid-turn / recently producing output) and a non-urgent restart defers. */
export const RESTART_IDLE_THRESHOLD_SECONDS = 120;
/** ...and an agent that received an inbound message within this many seconds is
 * also "busy" — it covers the gap where a message just arrived but the agent
 * hasn't produced pane output yet. Idle requires BOTH quiet. */
export const RESTART_INBOUND_QUIET_SECONDS = 300;
/**
 * ENG-6264: transcript-JSONL age (s) at/under which the session is "producing"
 * (actively streaming tokens) and a restart defers. This is the PRIMARY progress
 * signal — pane.log mtime is only the fallback, because an animated frozen
 * spinner keeps pane.log fresh without producing anything (the failure that let
 * the reaper interrupt busy agents). Mirrors the wedge detector's
 * `transcriptStaleSeconds` default (ENG-6238) for one consistent "busy" notion. */
export const RESTART_TRANSCRIPT_STALE_SECONDS = 60;

// Allowlist of RestartReason values that count as NON-URGENT maintenance and
// are therefore gated to the window + defer-until-idle. Anything not listed
// (day-rollover, auth-tuple-change recovery, manual/dashboard, or an
// undefined deprovision reason) BYPASSES the gate — fail-open by default so a
// new restart reason can never silently get trapped behind the window.
//
// ENG-6568: 'hot-reload-mcp' was REMOVED from this set. It is the class that
// fires when a newly-connected integration changes the .mcp.json server
// membership (and the managed-toolkit / channel-credential-rotation siblings).
// While gated it deferred until the maintenance window AND an idle gap, with no
// cap on the idle re-arm — so a busy agent could sit with the new toolkit
// unloaded indefinitely (valai ~30h; observed across vera/dwight/koda). A
// freshly-added integration silently looking "broken" until a manual bounce is
// a worse failure than interrupting a turn, so this class now BYPASSES the gate
// and respawns on the next poll (same promptness as a manual/dashboard restart).
// TRADEOFF: the window previously also damped the ENG-6123 managed-toolkit
// membership *oscillation* (ENG-6510, still recurring) from restarting customer
// agents repeatedly in business hours. With the window gone for this class, the
// ENG-5441 restart-breaker is the sole flap backstop (it trips + pauses after N
// restarts/window). The correct fix for the oscillation itself is ENG-6510, not
// re-gating integration loads.
const GATEABLE_RESTART_REASONS: ReadonlySet<string> = new Set([
  'model-change',
  'channel-set-change',
  'sender-policy-change',
  'channel-behaviour-change',
  'mcp-presence-reaper',
]);

/** Is this restart reason subject to the maintenance-window + idle gate? */
export function isGateableRestartReason(reason: string | undefined | null): boolean {
  return reason != null && GATEABLE_RESTART_REASONS.has(reason);
}

// ENG-6491: reasons that are gateable — still defer-until-idle, NEVER interrupt a
// live turn — but exempt from the off-peak maintenance WINDOW. An operator-
// initiated channel add must come online as soon as the agent is quiet, not hours
// later when the window opens (valai/gccm, ENG-6486: a Slack add sat deferred
// until the window opened). Scoped to `channel-set-change`: a channel add stays
// polite (waits for an idle gap) but skips the window.
//
// ENG-6568 note: `hot-reload-mcp` is no longer here OR in GATEABLE_RESTART_REASONS
// — integration loads now bypass the gate entirely (window AND idle), so they are
// not merely window-exempt. This set remains the "gated-but-window-exempt" middle
// tier for channel adds, which still defer until the agent is idle.
const WINDOW_EXEMPT_RESTART_REASONS: ReadonlySet<string> = new Set(['channel-set-change']);

/**
 * Is this restart reason exempt from the maintenance-WINDOW check while still
 * subject to the defer-until-idle gate? When true, `decideRestartGate` skips the
 * `defer-window` branch but the transcript/pane + inbound idle checks still apply.
 */
export function isWindowExemptRestartReason(reason: string | undefined | null): boolean {
  return reason != null && WINDOW_EXEMPT_RESTART_REASONS.has(reason);
}

/**
 * Gate a non-urgent restart-causing maintenance action.
 *  - 'proceed'      → restart now (in-window or gating disabled, AND idle).
 *  - 'defer-window' → outside the maintenance window; retry next window.
 *  - 'defer-idle'   → in-window but the agent is mid-turn / recently active
 *                     (pane.log younger than the idle threshold); retry soon.
 *
 * The window check fails open when no window is known yet (no heartbeat), but
 * defer-until-idle still applies — never interrupt a live turn.
 *
 * Idle requires BOTH the progress axis AND the inbound channel to be quiet.
 *  - Progress axis (ENG-6264): the session transcript JSONL is the primary
 *    "model is actually producing tokens" signal (transcript younger than
 *    `transcriptStaleSeconds` ⇒ busy), mirroring the wedge detector's
 *    `isSessionProducing` (ENG-6238). pane.log mtime is the FALLBACK, used only
 *    when the transcript age is null (no live session id / unstat-able file) —
 *    pane-primary mis-read a hung turn (static transcript, animated spinner) as
 *    busy, which is exactly what let the reaper never interrupt a wedged agent
 *    yet always interrupt a producing one.
 *  - Inbound axis (ENG-5974): a message arrived <`inboundQuietSeconds` ago but
 *    the agent hasn't produced output yet ⇒ busy.
 * "Busy" on either axis defers. A null age on an axis means "no signal" for it
 * → that axis is not busy, so the other governs. AGT_MAINTENANCE_WINDOW_DISABLE
 * bypasses BOTH gates.
 */
export function decideRestartGate(opts: {
  window: CachedMaintenanceWindow | null;
  paneLogAgeSeconds: number | null;
  inboundAgeSeconds: number | null;
  now: Date;
  env?: NodeJS.ProcessEnv;
  idleThresholdSeconds?: number;
  inboundQuietSeconds?: number;
  /**
   * ENG-6264: age (s) of the running session's transcript JSONL, or null when
   * it can't be resolved (no live session id / unstat-able file). When provided
   * non-null it is the PRIMARY busy signal; null falls back to pane.log age.
   */
  transcriptAgeSeconds?: number | null;
  transcriptStaleSeconds?: number;
  /**
   * ENG-6491: skip the maintenance-WINDOW check for this restart while STILL
   * applying the defer-until-idle gate below. Set for `channel-set-change`
   * (operator channel adds) so a newly-wired channel comes online as soon as the
   * agent is quiet rather than waiting for the off-peak window. Never short-
   * circuits the idle check — a live turn is still never interrupted.
   */
  windowExempt?: boolean;
}): 'proceed' | 'defer-window' | 'defer-idle' {
  if (isMaintenanceWindowDisabled(opts.env)) return 'proceed';
  if (!opts.windowExempt && opts.window && !isWithinMaintenanceWindow(opts.window, opts.now)) {
    return 'defer-window';
  }
  const paneThreshold = opts.idleThresholdSeconds ?? RESTART_IDLE_THRESHOLD_SECONDS;
  const inboundThreshold = opts.inboundQuietSeconds ?? RESTART_INBOUND_QUIET_SECONDS;
  const transcriptThreshold = opts.transcriptStaleSeconds ?? RESTART_TRANSCRIPT_STALE_SECONDS;
  // ENG-6264: progress axis — transcript growth is primary, pane.log mtime the
  // fallback when transcript age is null (mirrors ENG-6238 isSessionProducing).
  const transcriptAge = opts.transcriptAgeSeconds ?? null;
  const progressBusy = transcriptAge !== null
    ? transcriptAge < transcriptThreshold
    : opts.paneLogAgeSeconds !== null && opts.paneLogAgeSeconds < paneThreshold;
  const inboundBusy = opts.inboundAgeSeconds !== null && opts.inboundAgeSeconds < inboundThreshold;
  if (progressBusy || inboundBusy) return 'defer-idle';
  return 'proceed';
}
